import React from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { AuthenticationResponse } from 'data/schemas/auth';
import { TeamCalendarShareLink, TeamCalendarShareLinkCreateRequest } from 'data/schemas/TeamCalendar';
import { SingleEmployeePicker } from './EmployeePicker';

interface Props {
    isOpen: boolean;
    draft: TeamCalendarShareLinkCreateRequest;
    shareLinks: TeamCalendarShareLink[];
    employees: AuthenticationResponse[];
    availableTeams: string[];
    expiryDays: number;
    isSaving: boolean;
    onClose: () => void;
    onChange: <K extends keyof TeamCalendarShareLinkCreateRequest>(
        key: K,
        value: TeamCalendarShareLinkCreateRequest[K]
    ) => void;
    onExpiryDaysChange: (days: number) => void;
    onSubmit: () => void;
    onDelete: (shareLink: TeamCalendarShareLink) => void;
    onCopy: (shareLink: TeamCalendarShareLink) => void;
}

function ShareLinkModal(props: Props) {
    const {
        isOpen,
        draft,
        shareLinks,
        employees,
        availableTeams,
        expiryDays,
        isSaving,
        onClose,
        onChange,
        onExpiryDaysChange,
        onSubmit,
        onDelete,
        onCopy
    } = props;

    const formatDateValue = (value?: string) => (value ? value.slice(0, 10) : '');
    const formatScopeLabel = (shareLink: TeamCalendarShareLink) => {
        if (shareLink.scopeType === 'all') {
            return 'All teams';
        }

        if (shareLink.scopeType === 'team') {
            return `Team: ${shareLink.scopeValue || '-'}`;
        }

        if (shareLink.scopeType === 'person') {
            return `Person: ${shareLink.scopeValue || '-'}`;
        }

        return shareLink.scopeValue || '-';
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            size="lg"
        >
            <ModalHeader toggle={onClose}>Share Calendar</ModalHeader>
            <ModalBody>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Scope type</span>
                        <select
                            value={draft.scopeType}
                            onChange={event => onChange('scopeType', event.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                        >
                            <option value="all">All teams</option>
                            <option value="team">Specific team</option>
                            <option value="person">Specific person</option>
                        </select>
                    </label>
                    {draft.scopeType === 'team' && (
                        <label className="block">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Team</span>
                            <select
                                value={draft.scopeValue}
                                onChange={event => onChange('scopeValue', event.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2"
                            >
                                <option value="">Select team</option>
                                {availableTeams.map(team => (
                                    <option
                                        key={team}
                                        value={team}
                                    >
                                        {team}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    {draft.scopeType === 'person' && (
                        <label className="block">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Person</span>
                            <SingleEmployeePicker
                                value={draft.scopeValue}
                                employees={employees}
                                placeholder="Search employee"
                                onChange={citizenId => onChange('scopeValue', citizenId)}
                            />
                        </label>
                    )}
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Start date</span>
                        <input
                            type="date"
                            value={formatDateValue(draft.startDate)}
                            onChange={event =>
                                onChange(
                                    'startDate',
                                    event.target.value ? `${event.target.value}T00:00:00.000Z` : ''
                                )
                            }
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">End date</span>
                        <input
                            type="date"
                            value={formatDateValue(draft.endDate)}
                            onChange={event =>
                                onChange(
                                    'endDate',
                                    event.target.value ? `${event.target.value}T23:59:59.999Z` : ''
                                )
                            }
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                        />
                    </label>
                    <label className="block md:col-span-2">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Expire in</span>
                        <select
                            value={expiryDays}
                            onChange={event => onExpiryDaysChange(Number(event.target.value))}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                        >
                            {Array.from({ length: 14 }, (_, index) => index + 1).map(days => (
                                <option
                                    key={days}
                                    value={days}
                                >
                                    {days} day{days > 1 ? 's' : ''}
                                </option>
                            ))}
                        </select>
                        <div className="mt-1 text-xs text-gray-500">
                            Link expires on {formatDateValue(draft.expiresAt) || '-'}
                        </div>
                    </label>
                </div>

                <div className="mt-5 flex justify-end">
                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={isSaving}
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isSaving ? 'Creating...' : 'Create share link'}
                    </button>
                </div>

                <div className="mt-6">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                        Existing Links
                    </h3>
                    <div className="space-y-3">
                        {shareLinks.length === 0 && (
                            <p className="text-sm text-gray-500">No share links created yet.</p>
                        )}
                        {shareLinks.map(shareLink => (
                            <div
                                key={shareLink.id}
                                className="rounded-lg border border-gray-200 p-3"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <div className="font-semibold text-gray-800">
                                            {formatScopeLabel(shareLink)}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Range: {formatDateValue(shareLink.startDate) || '-'} to{' '}
                                            {formatDateValue(shareLink.endDate) || '-'}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Expires: {formatDateValue(shareLink.expiresAt) || '-'}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => onCopy(shareLink)}
                                            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                                        >
                                            Copy
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onDelete(shareLink)}
                                            className="rounded-md border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}

export default ShareLinkModal;
